/** Países para máscara europeia de 6 caracteres agrupados em pares (ex: AA-00-AA, 50-VS-16). */
const EU_PLATE_COUNTRIES = new Set(['PT', 'ES', 'FR', 'DE']);

/**
 * Normaliza código ISO 3166-1 alpha-2. Vazio ou inválido → `BR`.
 */
export function normalizeCountryCode(code: string | null | undefined): string {
    const s = String(code ?? '')
        .trim()
        .toUpperCase();
    if (!s || s.length !== 2) {
        return 'BR';
    }
    return s;
}

/** `true` se a máscara/validação deve seguir o formato AA-00-AA; caso contrário Mercosul (SSS-0A00). */
export function usesEuropeanPlateMaskForCountry(iso2: string | null | undefined): boolean {
    return EU_PLATE_COUNTRIES.has(normalizeCountryCode(iso2));
}

function stripPlateAlnum(raw: string | null | undefined): string {
    return String(raw ?? '')
        .replace(/[^A-Za-z0-9]/g, '')
        .toUpperCase();
}

/** Placa válida no formato europeu (6 caracteres alfanuméricos, sem hífen). */
export function isEuropeanPlateStripped(stripped: string): boolean {
    return /^[A-Z0-9]{6}$/.test(stripped);
}

/** Placa válida no formato Mercosul (7 caracteres, sem hífen). */
export function isMercosulPlateStripped(stripped: string): boolean {
    return /^[A-Z]{3}\d[A-Z0-9]\d{2}$/.test(stripped);
}

export function isLicensePlateCompleteForCountry(raw: string | null | undefined, countryIso2: string | null | undefined): boolean {
    const s = stripPlateAlnum(raw);
    if (!s) {
        return false;
    }
    if (usesEuropeanPlateMaskForCountry(countryIso2)) {
        return isEuropeanPlateStripped(s);
    }
    return isMercosulPlateStripped(s);
}

/** Exibe EU incompleta. Agrupa a cada 2 caracteres. */
function formatEuIncompleteFromStripped(s: string): string {
    let r = '';
    for (let i = 0; i < s.length; i++) {
        if (i > 0 && i % 2 === 0 && i < 6) {
            r += '-';
        }
        r += s[i];
    }
    return r;
}

/** Exibe Mercosul incompleta (ABC-1X23 → ok; EU sob máscara BR → ex. AB-). */
function formatMercosulIncompleteFromStripped(s: string): string {
    let i = 0;
    const takeLetters = (max: number) => {
        let r = '';
        while (r.length < max && i < s.length && /[A-Z]/.test(s[i])) {
            r += s[i++];
        }
        return r;
    };
    const p0 = takeLetters(3);
    if (p0.length < 3) {
        return p0.length ? `${p0}-` : '';
    }
    if (i >= s.length || !/[0-9]/.test(s[i])) {
        return `${p0}-`;
    }
    let tail = s[i++];
    if (i >= s.length || !/[A-Z0-9]/.test(s[i])) {
        return `${p0}-${tail}-`;
    }
    tail += s[i++];
    let d = '';
    while (d.length < 2 && i < s.length && /[0-9]/.test(s[i])) {
        d += s[i++];
    }
    tail += d;
    if (d.length < 2) {
        return `${p0}-${tail}-`;
    }
    return `${p0}-${tail}`;
}

/**
 * Formata a placa para exibição conforme o país da empresa (máscara atual).
 * Se o valor salvo não combina com esse formato (ex. troca de país), mostra preenchimento parcial e hífens (ex. AB-1-).
 */
export function formatLicensePlateForCountryDisplay(raw: string | null | undefined, countryIso2: string | null | undefined): string {
    const s = stripPlateAlnum(raw);
    if (!s) {
        return '';
    }
    if (usesEuropeanPlateMaskForCountry(countryIso2)) {
        if (isEuropeanPlateStripped(s)) {
            return `${s.slice(0, 2)}-${s.slice(2, 4)}-${s.slice(4, 6)}`;
        }
        return formatEuIncompleteFromStripped(s);
    }
    if (isMercosulPlateStripped(s)) {
        return `${s.slice(0, 3)}-${s.slice(3)}`;
    }
    return formatMercosulIncompleteFromStripped(s);
}
